/**
 * Tolerance check for the "unexpected loss of authentication" recovery path.
 *
 * Background: AuthProvider's session-sync effect previously reacted to ANY
 * transient `logtoIsAuthenticated=false` while `sessionState=AUTHENTICATED`
 * by clearing tokens and opening the SessionExpiredDrawer. This is too eager
 * — the Logto SDK can briefly report `isAuthenticated=false` during a
 * legitimate token-refresh round-trip, a cross-tab broadcast, or a WebView
 * focus event. Each false positive cleared real tokens and ended up looking
 * to the user like a spurious sign-out.
 *
 * The fix wraps that path in a tolerance window. The new effect schedules a
 * timer; when the timer fires, it calls `shouldFireRecovery()` to decide
 * whether to actually clear tokens and open the drawer, or to skip and let
 * the SDK keep doing its thing.
 *
 * This predicate is the single point of truth for "did the SDK actually
 * lose auth, or was that a transient blip?". It runs AFTER the tolerance
 * window has already elapsed (typically ~1.5s), so by this point a
 * legitimate transient should have resolved itself.
 *
 * Inputs are passed by value (snapshot at timer-fire time). The caller is
 * responsible for reading current state via refs into this struct.
 */
export interface ShouldFireRecoveryInputs {
  /**
   * Latest value from `useLogtoAdapter().isAuthenticated`. If true, the SDK
   * recovered during the tolerance window — auth is fine.
   */
  logtoIsAuthenticated: boolean;
  /**
   * Latest value from `useLogtoAdapter().isLoading`. If true, the SDK is
   * mid-operation (e.g., a refresh round-trip is still in flight). Firing
   * recovery now would cancel an operation that might be about to succeed.
   */
  logtoIsLoading: boolean;
}

/**
 * Decision rules:
 *
 *   1. If `logtoIsAuthenticated === true` → SKIP. The SDK recovered during
 *      the tolerance window. This is the most common false-positive cause
 *      and the original bug we're fixing.
 *
 *   2. If `logtoIsLoading === true` → SKIP. The SDK is still mid-operation
 *      (a refresh request is in flight, or on Capacitor a system-browser
 *      round-trip is unfinished). Firing recovery here would convert a
 *      potentially-successful refresh into a hard sign-out, and the
 *      Capacitor adapter is known to keep `isLoading` true for several
 *      seconds during legitimate flows. Defer to the next dep change —
 *      when loading finishes, the effect re-runs and re-evaluates.
 *
 *   3. Otherwise → FIRE. Logto is conclusively settled (`isLoading=false`)
 *      and unauthenticated for the full tolerance window.
 *
 * The predicate operates on `(isAuthenticated, isLoading)` only because
 * `invalid_grant` is intercepted upstream by `useInvalidGrantHandler`
 * (which runs from `getTokenForApi` and `useProactiveTokenRefresh`). Any
 * non-grant SDK error reaching this point is either a transient that
 * rule 2 covers if `isLoading` flips true, or a stable failure that's
 * indistinguishable from a real expiration — adding a branch on it
 * would not change user-visible behavior.
 */
export function shouldFireRecovery(inputs: ShouldFireRecoveryInputs): boolean {
  if (inputs.logtoIsAuthenticated) return false;
  if (inputs.logtoIsLoading) return false;
  return true;
}
